import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../../lib/api";

type AttachmentMeta = {
  filename: string;
  contentType: string;
  size: number;
  partId: string;
};

function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function isAttachmentPreviewable(contentType: string) {
  if (contentType.startsWith("image/")) return true;
  if (contentType.startsWith("video/")) return true;
  if (contentType === "application/pdf") return true;
  if (contentType.startsWith("text/")) return true;
  return false;
}

function sanitizeDownloadName(name: string | null | undefined, fallback: string) {
  const raw = (name || "").trim() || fallback;
  const cleaned = raw
    .replace(/[/\\?%*:|"<>]/g, "_")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 180);
  return cleaned || fallback || "allegato";
}

export function AttachmentPreviewModal({
  accountId,
  folder,
  uid,
  attachment,
  onClose,
}: {
  accountId: string;
  folder: string;
  uid: number;
  attachment: AttachmentMeta;
  onClose: () => void;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [textPreview, setTextPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const previewable = isAttachmentPreviewable(attachment.contentType);

  useEffect(() => {
    document.body.classList.add("ms-confirm-open");
    return () => document.body.classList.remove("ms-confirm-open");
  }, []);

  useEffect(() => {
    let revoked: string | null = null;
    let cancelled = false;
    setLoading(true);
    setError("");
    setBlobUrl(null);
    setTextPreview(null);

    void (async () => {
      try {
        const { blob } = await api.attachmentBlob(accountId, folder, uid, attachment.partId);
        if (cancelled) return;
        if (attachment.contentType.startsWith("text/")) {
          const text = await blob.text();
          if (!cancelled) setTextPreview(text.slice(0, 120_000));
        } else if (previewable) {
          revoked = URL.createObjectURL(blob);
          setBlobUrl(revoked);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [accountId, folder, uid, attachment.partId, attachment.contentType, previewable]);

  async function download() {
    try {
      const { blob, filename: headerName } = await api.attachmentBlob(
        accountId,
        folder,
        uid,
        attachment.partId,
        true,
      );
      const name = sanitizeDownloadName(
        headerName || attachment.filename,
        `allegato-${attachment.partId}`,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Keep blob URL briefly — some browsers ignore download if revoked immediately
      window.setTimeout(() => URL.revokeObjectURL(url), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return createPortal(
    <div className="modal-back attachment-preview-back" onClick={onClose}>
      <div className="modal attachment-preview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="attachment-preview-head">
          <div>
            <div className="eyebrow">Allegato</div>
            <strong>{attachment.filename}</strong>
            <div className="attachment-preview-meta">
              {attachment.contentType} · {formatBytes(attachment.size)}
            </div>
          </div>
          <div className="attachment-preview-actions">
            <button type="button" className="btn btn-primary" onClick={() => void download()}>
              Scarica
            </button>
            <button type="button" className="btn btn-ghost" onClick={onClose} aria-label="Chiudi">
              ✕
            </button>
          </div>
        </div>
        <div className="attachment-preview-body">
          {loading && <p className="attachment-preview-status">Caricamento…</p>}
          {error && <p className="error">{error}</p>}
          {!loading && !error && !previewable && (
            <div className="attachment-preview-fallback">
              <p>Anteprima non disponibile per questo tipo di file.</p>
              <button type="button" className="btn btn-primary" onClick={() => void download()}>
                Scarica file
              </button>
            </div>
          )}
          {!loading && !error && blobUrl && attachment.contentType.startsWith("image/") && (
            <img src={blobUrl} alt={attachment.filename} className="attachment-preview-image" />
          )}
          {!loading && !error && blobUrl && attachment.contentType.startsWith("video/") && (
            <video src={blobUrl} controls className="attachment-preview-video" />
          )}
          {!loading && !error && blobUrl && attachment.contentType === "application/pdf" && (
            <iframe src={blobUrl} title={attachment.filename} className="attachment-preview-pdf" />
          )}
          {!loading && !error && textPreview !== null && (
            <pre className="attachment-preview-text">{textPreview}</pre>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
